import React, { useState, useEffect, useCallback } from "react";
import { DocumentSnapshot, Timestamp } from "firebase/firestore";
import { Submission } from "../../types/events";
import ContentCard from "../dashboard/ContentCard";
import InfiniteScroll from "react-infinite-scroll-component";
import LoadingIndicator from "../ui/LoadingIndicator";
import PreviewModal from "../library/PreviewModal";
import { LibraryItem } from "../../types/library";
import { ContentItem } from "../../utils/dummyData";
import dreamWorldProvider from "../../DreamWorldProvider";

interface EventSubmissionsProps {
  eventId: string;
}

const EventSubmissions: React.FC<EventSubmissionsProps> = ({ eventId }) => {
  const [submissions, setSubmissions] = useState<Submission[]>([]);
  const [lastVisible, setLastVisible] = useState<DocumentSnapshot | null>(null);
  const [hasMore, setHasMore] = useState(true);
  const [isLoading, setIsLoading] = useState(false);
  const [selectedSubmission, setSelectedSubmission] =
    useState<Submission | null>(null);

  const fetchSubmissions = useCallback(
    async (initial = false) => {
      if (isLoading || !hasMore) return;
      setIsLoading(true);

      try {
        const { submissions: fetchedSubmissions, lastVisible: newLastVisible } =
          await dreamWorldProvider.getEventSubmissions(
            eventId,
            initial ? undefined : lastVisible
          );

        setSubmissions((prev) =>
          initial ? fetchedSubmissions : [...prev, ...fetchedSubmissions]
        );
        setLastVisible(newLastVisible);
        setHasMore(fetchedSubmissions.length > 0);
      } catch (error) {
        console.error("Error fetching submissions:", error);
      } finally {
        setIsLoading(false);
      }
    },
    [eventId, isLoading, hasMore, lastVisible]
  );

  useEffect(() => {
    fetchSubmissions(true);
  }, [fetchSubmissions]);

  const handleViewSubmission = (submission: Submission) => {
    setSelectedSubmission(submission);
  };

  const mapSubmissionToLibraryItem = (submission: Submission): LibraryItem => {
    // TODO: Fetch full author data from Firestore
    return {
      id: submission.id,
      ownerId: submission.userId,
      title: submission.title,
      type: "Image", // Assuming all submissions are images for now
      fullUrl: submission.contentUrl,
      thumbnailUrl: submission.image,
      createdAt: submission.createdAt,
      isFavorite: false,
      views: submission.views,
      likes: submission.likes,
      tags: [],
      size: "",
      isPublished: true,
      description: submission.description,
      prompt: "",
      creator: {
        uid: submission.userId,
        username: submission.author,
        avatarUrl: "",
        email: "",
        createdAt: 0,
      },
    };
  };

  const mapSubmissionToContentItem = (submission: Submission): ContentItem => {
    return {
      id: submission.id,
      type: "Image",
      title: submission.title,
      category: "",
      creator: {
        name: submission.author,
        avatarUrl: "",
      },
      likes: submission.likes,
      date: submission.createdAt.toDate().toISOString(),
      thumbnailUrl: submission.image,
      fullUrl: submission.contentUrl,
      prompt: submission.description,
      isFavorite: false,
    };
  };

  return (
    <div>
      <InfiniteScroll
        dataLength={submissions.length}
        next={() => fetchSubmissions()}
        hasMore={hasMore}
        loader={<LoadingIndicator />}
        endMessage={
          <p className="text-center text-gray-400 mt-8">
            No more submissions to load.
          </p>
        }
        className="grid grid-cols-2 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-1.5 px-1"
      >
        {submissions.map((submission) => (
          <ContentCard
            key={submission.id}
            item={mapSubmissionToLibraryItem(submission)}
            onLike={() => {}}
            onClick={() => handleViewSubmission(submission)}
            viewMode="grid"
          />
        ))}
      </InfiniteScroll>

      {selectedSubmission && (
        <PreviewModal
          item={mapSubmissionToContentItem(selectedSubmission)}
          onClose={() => setSelectedSubmission(null)}
        />
      )}
    </div>
  );
};

export default EventSubmissions;
